import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { PencilSquareIcon, TrashIcon, QrCodeIcon, PrinterIcon, TableCellsIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import 'styles/TableComponent.css' // replace with tailwind

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50]

const TableComponent = ({
  tableData,
  handleEdit = null,
  handleDelete = null,
  handleQRClick = null,
  handlePrintClick = null,
  defaultSortColumn = '',
  defaultSortOrder = 'asc',
  onSelectionChange = null,
  onRowClick = null,
  exportable = true,
  exportFileName = 'table',
  exportTitle = '',
  exportSubtitle = ''
}) => {
  const { t } = useTranslation()
  if (!tableData || !Array.isArray(tableData.columns) || !Array.isArray(tableData.rows)) {
    return <div>{t('common.noData')}</div>
  }

  // A column header may carry an i18n key (labelKey) or a plain string (label,
  // used by not-yet-migrated management tables). Prefer the translated key.
  const columnLabel = col => (col.labelKey ? t(col.labelKey) : col.label)

  const [selectedRows, setSelectedRows] = useState([])
  const hasCheckbox = tableData.hasCheckbox || false
  const hasActions = typeof handleEdit === 'function' || typeof handleDelete === 'function' || typeof handleQRClick === 'function'

  const [state, setState] = useState({
    columnName: defaultSortColumn,
    orderBy: defaultSortOrder
  })

  // Separate totals row from regular data
  const regularRows = tableData.rows.filter(row => !row?.isTotal)
  const totalsRows = tableData.rows.filter(row => row?.isTotal)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const totalRows = regularRows.length
  const totalPages = Math.ceil(totalRows / rowsPerPage)

  // handle sort by column
  const handleSort = (key) => {
    const direction = state.columnName === key ? (state.orderBy.toLowerCase() === 'asc' ? 'desc' : 'asc') : 'asc'
    setState(s => ({ ...s, columnName: key, orderBy: direction }))
    setCurrentPage(1) // Reset to first page on sort
  }

  const getValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj)
  }

  const getSortedRows = () => {
    // If no column is selected for sorting, return original order
    if (!state.columnName) return regularRows

    return [...regularRows].sort((a, b) => {
      const aVal = getValue(a, state.columnName)
      const bVal = getValue(b, state.columnName)

      if (aVal === undefined || bVal === undefined) return 0

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return state.orderBy === 'asc' ? aVal - bVal : bVal - aVal
      }

      const aNum = Number(aVal)
      const bNum = Number(bVal)
      if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
        return state.orderBy === 'asc' ? aNum - bNum : bNum - aNum
      }

      return state.orderBy === 'asc'
        ? String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' })
        : String(bVal).localeCompare(String(aVal), undefined, { sensitivity: 'base' })
    })
  }

  // Pagination: get rows for current page
  const getPaginatedRows = () => {
    const sortedRows = getSortedRows()
    const startIdx = (currentPage - 1) * rowsPerPage
    return sortedRows.slice(startIdx, startIdx + rowsPerPage)
  }

  // --- Export (client-side) -------------------------------------------------
  // Data columns only (the 'actions' column carries buttons, no data).
  const exportColumns = tableData.columns.filter(col => col.key !== 'actions')

  // Plain-text value of a cell for export: prefer the column's rendered value
  // when it is a primitive (e.g. 'Yes'/'No', formatted numbers), otherwise the
  // raw field; arrays are joined and JSX/objects fall back to empty.
  const exportCellText = (col, row) => {
    const value = row?.[col.key]
    if (typeof col.cell === 'function') {
      const rendered = col.cell(value, row)
      if (typeof rendered === 'string' || typeof rendered === 'number') return String(rendered)
    }
    if (Array.isArray(value)) return value.join(', ')
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') return ''
    return String(value)
  }

  // Export the FULL current table content (all rows in current sort + totals),
  // not just the visible page.
  const buildExportMatrix = () => {
    const rowsToExport = [...getSortedRows(), ...totalsRows]
    const header = exportColumns.map(col => columnLabel(col))
    const body = rowsToExport.map(row => exportColumns.map(col => exportCellText(col, row)))
    return { header, body }
  }

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Optional title lines (report name, applied filters) printed above the
  // table header in both exports.
  const exportHeadingLines = [exportTitle, exportSubtitle].filter(Boolean)

  const handleExportExcel = () => {
    const { header, body } = buildExportMatrix()
    const worksheet = XLSXUtils.aoa_to_sheet([...exportHeadingLines.map(line => [line]), header, ...body])
    worksheet['!merges'] = exportHeadingLines.map((_, row) => ({
      s: { r: row, c: 0 },
      e: { r: row, c: Math.max(header.length - 1, 0) }
    }))
    const workbook = XLSXUtils.book_new()
    XLSXUtils.book_append_sheet(workbook, worksheet, 'Data')
    const buffer = XLSXWrite(workbook, { bookType: 'xlsx', type: 'array' })
    triggerDownload(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `${exportFileName}.xlsx`
    )
  }

  const handleExportPdf = () => {
    const { header, body } = buildExportMatrix()
    // eslint-disable-next-line new-cap
    const doc = new JsPDF({ orientation: header.length > 6 ? 'landscape' : 'portrait' })
    let startY
    if (exportHeadingLines.length > 0) {
      doc.setFontSize(13)
      doc.text(exportHeadingLines[0], 14, 15)
      doc.setFontSize(10)
      exportHeadingLines.slice(1).forEach((line, idx) => doc.text(line, 14, 22 + idx * 6))
      startY = 16 + exportHeadingLines.length * 6
    }
    doc.autoTable({
      startY,
      head: [header],
      body,
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [46, 125, 50] }
    })
    doc.save(`${exportFileName}.pdf`)
  }
  // --------------------------------------------------------------------------

  // handle selecting individual rows
  const handleRowSelect = (rowIndex) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowIndex) ? prevSelected.filter((index) => index !== rowIndex) : [...prevSelected, rowIndex]
    )
  }

  // handle selecting all rows
  const handleSelectAll = () => {
    const paginatedRows = getPaginatedRows()
    const paginatedIndexes = paginatedRows.map((_, idx) => (currentPage - 1) * rowsPerPage + idx)
    const allSelected = paginatedIndexes.every(idx => selectedRows.includes(idx))
    setSelectedRows(allSelected
      ? selectedRows.filter(idx => !paginatedIndexes.includes(idx))
      : [...selectedRows, ...paginatedIndexes.filter(idx => !selectedRows.includes(idx))]
    )
  }

  // Notify parent when selection changes
  useEffect(() => {
    if (!onSelectionChange) return
    const sortedRows = getSortedRows()
    const selectedRowObjects = selectedRows.map(idx => sortedRows[idx]).filter(Boolean)
    onSelectionChange(selectedRowObjects)
  }, [selectedRows])

  // render checkbox in header only if hasCheckbox is true
  const renderCheckboxHead = () => (
    <th>
      <input
        type='checkbox'
        onChange={handleSelectAll}
        checked={
          getPaginatedRows().length > 0 &&
          getPaginatedRows().every((_, idx) => selectedRows.includes((currentPage - 1) * rowsPerPage + idx))
        }
      />
    </th>
  )

  // render checkbox in column only if hasCheckbox is true
  const renderCheckboxBody = (rowIndex) => (
    <td>
      <input
        type='checkbox'
        checked={selectedRows.includes(rowIndex)}
        onChange={() => handleRowSelect(rowIndex)}
      />
    </td>
  )

  // render cell content, if array paints as list or direct value
  const renderCellContent = (cellData) => {
    if (Array.isArray(cellData)) {
      return (
        <ul style={{ paddingLeft: '16px', margin: 0 }}>
          {cellData.length > 0 ? cellData.map((item, index) => <li key={index}>{item}</li>) : <li>-</li>}
        </ul>
      )
    }
    return cellData ?? '-'
  }

  // renders table data
  const renderTableData = (row) =>
    tableData.columns.map((col, colIndex) => {
      const value = row?.[col.key]

      if (col.key === 'actions') {
        if (typeof col.cell === 'function') {
          return (
            <td key={colIndex}>
              {col.cell(value, row)}
            </td>
          )
        }

        if (hasActions) {
          return (
            <td key={colIndex}>
              <div className='flex items-center gap-2'>
                {handleEdit && (
                  <button onClick={() => handleEdit(row)} title={t('common.edit')} className='p-1 rounded transition'>
                    <PencilSquareIcon className='w-5 h-5 text-blue-500' />
                  </button>
                )}
                {handleDelete && (
                  <button onClick={() => handleDelete(row)} title={t('common.delete')} className='p-1 rounded transition'>
                    <TrashIcon className='w-5 h-5 text-red-500' />
                  </button>
                )}
                {handleQRClick && (
                  <button onClick={() => handleQRClick(row)} title={t('common.qrCode')} className='p-1 rounded transition'>
                    <QrCodeIcon className='w-5 h-5 text-gray-600' />
                  </button>
                )}
                {handlePrintClick && (
                  <button onClick={() => handlePrintClick(row)} title={t('common.print')} className='p-1 rounded transition'>
                    <PrinterIcon className='w-5 h-5 text-gray-600' />
                  </button>
                )}
              </div>
            </td>
          )
        }

        return <td key={colIndex}>-</td>
      }

      return (
        <td key={colIndex}>
          {col.cell ? col.cell(value, row) : renderCellContent(value)}
        </td>
      )
    })

  // Pagination controls with rows per page selector
  const renderPagination = () => (
    <div className='flex items-center justify-start mt-2 gap-2 text-sm'>
      <span>{t('common.table.rows')}</span>
      <select
        className='border rounded px-1 py-0.5'
        value={rowsPerPage}
        onChange={e => {
          setRowsPerPage(Number(e.target.value))
          setCurrentPage(1)
        }}
      >
        {ROWS_PER_PAGE_OPTIONS.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <button
        className='px-2 py-1 bg-gray-200 rounded disabled:opacity-50'
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        {t('common.table.prev')}
      </button>
      <span>
        {t('common.table.page', { current: currentPage, total: totalPages })}
      </span>
      <button
        className='px-2 py-1 bg-gray-200 rounded disabled:opacity-50'
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        {t('common.table.next')}
      </button>
    </div>
  )

  return (
    <div className='overflow-x-auto max-w-5l'>
      {exportable && exportColumns.length > 0 && regularRows.length > 0 && (
        <div className='flex justify-end items-center gap-1 mb-1'>
          <button
            type='button'
            onClick={handleExportExcel}
            title={t('common.exportToExcel')}
            aria-label={t('common.exportToExcel')}
            className='p-1 rounded hover:bg-gray-100 transition-colors'
          >
            <TableCellsIcon className='w-4 h-4 text-green-600' />
          </button>
          <button
            type='button'
            onClick={handleExportPdf}
            title={t('common.exportToPdf')}
            aria-label={t('common.exportToPdf')}
            className='p-1 rounded hover:bg-gray-100 transition-colors'
          >
            <DocumentArrowDownIcon className='w-4 h-4 text-red-500' />
          </button>
        </div>
      )}
      <div className='table-container table-wrapper'>
        <table className='table w-full border-rounded-lg border border-gray-200 text-left'>
          <thead>
            <tr className='cursor-pointer text-greenPrimary hover:bg-gray-100 transition-colors duration-200'>
              {hasCheckbox && renderCheckboxHead()}
              {tableData.columns.map((col, index) => (
                <th
                  onClick={() => handleSort(col.key)}
                  key={index}
                  className='hover:bg-gray-200 transition-colors duration-150'
                >
                  {columnLabel(col)} {col.key !== 'actions' && state.columnName === col.key && (<span>{state.orderBy === 'asc' ? ' ↑' : ' ↓'}</span>)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Regular data rows */}
            {getPaginatedRows().map((row, idx) => {
              const globalRowIndex = (currentPage - 1) * rowsPerPage + idx
              return row
                ? (
                  <tr
                    key={globalRowIndex}
                    className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {hasCheckbox && renderCheckboxBody(globalRowIndex)}
                    {renderTableData(row)}
                  </tr>
                  )
                : null
            })}
            {/* Totals rows - always at the bottom */}
            {totalsRows.map((row, idx) => {
              const totalsRowIndex = `totals-${idx}`
              return (
                <tr key={totalsRowIndex} className='font-bold bg-gray-100 border-t-2 border-gray-400'>
                  {hasCheckbox && <td />}
                  {renderTableData(row)}
                </tr>
              )
            })}
          </tbody>
        </table>
        {totalPages > 1 && renderPagination()}
      </div>
    </div>
  )
}

TableComponent.propTypes = {
  tableData: PropTypes.shape({
    hasCheckbox: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
        labelKey: PropTypes.string,
        cell: PropTypes.func
      })
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleQRClick: PropTypes.func,
  onRowClick: PropTypes.func,
  exportable: PropTypes.bool,
  exportFileName: PropTypes.string,
  exportTitle: PropTypes.string,
  exportSubtitle: PropTypes.string
}

export default TableComponent
